import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaFileExcel, FaArrowRight, FaArrowLeft, FaUpload, FaPlay,
  FaPause, FaDownload, FaTrashAlt, FaExclamationTriangle, FaCheckCircle,
  FaChevronRight, FaLightbulb, FaClock, FaComments
} from "react-icons/fa";

export default function SendViaExcelGuide() {
  useEffect(() => {
    document.title = "Send via Excel Guide | Walytic - Bulk WhatsApp Campaigns from Spreadsheet";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="Send via Excel Guide" subtitle="Upload a spreadsheet, preview your contacts, and launch a WhatsApp campaign in minutes." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'getting-started', label: 'Getting started' },
              { id: 'file-format', label: 'File format and columns' },
              { id: 'preview-and-send', label: 'Preview and send' },
              { id: 'campaign-history', label: 'Campaign history table' },
              { id: 'actions', label: 'Actions: Export, Pause, Resume, Delete' },
              { id: 'tips', label: 'Tips and limits' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: Getting Started */}
        <section id="getting-started" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Getting started</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Send via Excel lets you run a WhatsApp campaign from a spreadsheet file. You pick a session, name
            your campaign, upload a file, and hit send. Walytic handles the rest.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Select a WhatsApp session</h3>
                <p className="text-ink-secondary">
                  Open <strong>Send via Excel</strong> from the sidebar. Choose a connected WhatsApp session from the dropdown.
                  Only sessions with an active QR scan appear here.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Enter a campaign name</h3>
                <p className="text-ink-secondary">
                  Give your campaign a descriptive name like &quot;April Promo&quot; or &quot;Webinar Invite - Batch 2&quot;.
                  This name shows in your campaign history for easy tracking.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Upload your file</h3>
                <p className="text-ink-secondary">
                  Click the upload area or drag-and-drop a <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.xlsx</code>,
                  {' '}<code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.xls</code>, or
                  {' '}<code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.csv</code> file.
                  Walytic reads the first sheet and parses it instantly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: File Format */}
        <section id="file-format" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">File format and columns</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Row 1 must be headers. Walytic looks for these column names (case-insensitive):
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">Required</span>
                <h3 className="font-semibold text-lg text-ink-primary">number</h3>
              </div>
              <p className="text-ink-secondary">
                Phone number with country code, no + sign, no spaces or dashes.
                Example: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">919876543210</code> for an Indian number,
                {' '}<code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">447911123456</code> for a UK number.
              </p>
            </div>

            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-ink-primary font-medium">Optional</span>
                <h3 className="font-semibold text-lg text-ink-primary">message</h3>
              </div>
              <p className="text-ink-secondary">
                Custom message for that specific row. If this column exists and has a value, it overrides
                the Default Message. Rows with an empty message cell fall back to the Default Message.
              </p>
            </div>

            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-ink-primary font-medium">Optional</span>
                <h3 className="font-semibold text-lg text-ink-primary">mediaUrl</h3>
              </div>
              <p className="text-ink-secondary">
                A public URL to an image, PDF, or document to attach with the message. Use the
                {' '}<Link to="/guide/image-to-url" className="text-brand underline">Image to URL</Link> feature
                to generate shareable links if needed.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-ink-primary mb-2">Example spreadsheet:</p>
            <div className="overflow-x-auto">
              <table className="text-sm text-ink-secondary w-full">
                <thead>
                  <tr className="border-b border-surface-border">
                    <th className="text-left py-2 pr-6 font-medium">number</th>
                    <th className="text-left py-2 pr-6 font-medium">message</th>
                    <th className="text-left py-2 font-medium">mediaUrl</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 pr-6">919876543210</td>
                    <td className="py-2 pr-6">Hi Rahul, check this out!</td>
                    <td className="py-2">https://files.example.com/promo.pdf</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 pr-6">447911123456</td>
                    <td className="py-2 pr-6"></td>
                    <td className="py-2"></td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-6">12025551234</td>
                    <td className="py-2 pr-6">Special offer just for you</td>
                    <td className="py-2"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-ink-muted mt-2">
              Row 2 has no message or media -- it will use the Default Message you set during preview.
            </p>
          </div>
        </section>

        {/* Section: Preview and Send */}
        <section id="preview-and-send" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Preview and send</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            After uploading, Walytic shows a preview of the first 10 rows from your file.
            Double-check that numbers and messages look correct before proceeding.
          </p>

          <div className="space-y-6">
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaComments className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Default Message</h3>
              </div>
              <p className="text-ink-secondary">
                An optional text field that appears below the preview. Any row without a value in the
                <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm mx-1">message</code>
                column receives this default text instead. If you leave this blank and a row has no message,
                that row is skipped.
              </p>
            </div>

            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaPlay className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Start Campaign</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Click <strong>&quot;Start Campaign&quot;</strong> to begin sending. Messages go out with smart
                delays between each send to reduce the risk of WhatsApp flagging your number.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <FaExclamationTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    Once started, the campaign runs in the background. You can close the browser tab --
                    sending continues on the server until all messages are delivered or the campaign is paused.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Campaign History */}
        <section id="campaign-history" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Campaign history table</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Every campaign you run shows up in the history table at the bottom of the page. Here is what each column means:
          </p>

          <div className="bg-surface-card rounded-xl border border-surface-border overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Column</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Description</th>
                </tr>
              </thead>
              <tbody className="text-ink-secondary">
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Campaign Name</td>
                  <td className="py-3 px-4">The name you entered before sending</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Total</td>
                  <td className="py-3 px-4">Total number of recipients in the uploaded file</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Delivered %</td>
                  <td className="py-3 px-4">Percentage of messages successfully sent</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Failed</td>
                  <td className="py-3 px-4">Count of messages that could not be delivered (invalid number, phone off, etc.)</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Status</td>
                  <td className="py-3 px-4">Completed, Processing, Scheduled, Paused, or Failed</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section: Actions */}
        <section id="actions" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Actions: Export, Pause, Resume, Delete</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Each campaign row has action buttons on the right side:
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaDownload className="h-4 w-4 text-brand" />
                <h4 className="font-semibold text-sm text-ink-primary">Export Results</h4>
              </div>
              <p className="text-sm text-ink-secondary">
                Downloads an Excel file with every recipient, their delivery status, and timestamps.
                Useful for follow-up analysis.
              </p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaPause className="h-4 w-4 text-amber-600" />
                <h4 className="font-semibold text-sm text-ink-primary">Pause</h4>
              </div>
              <p className="text-sm text-ink-secondary">
                Stops the campaign mid-send. Remaining messages stay queued but are not sent
                until you resume.
              </p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaPlay className="h-4 w-4 text-green-600" />
                <h4 className="font-semibold text-sm text-ink-primary">Resume</h4>
              </div>
              <p className="text-sm text-ink-secondary">
                Picks up where the campaign left off. Already-sent messages are not re-sent.
              </p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaTrashAlt className="h-4 w-4 text-red-600" />
                <h4 className="font-semibold text-sm text-ink-primary">Delete</h4>
              </div>
              <p className="text-sm text-ink-secondary">
                Removes the campaign and its history permanently. Cannot be undone.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">Tips and limits</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Stay under 150 messages/day', text: 'Per session, keep daily sends under 150. Spreading across multiple sessions lets you scale safely.' },
              { title: 'WhatsApp formatting works', text: 'Use *bold* for bold, _italic_ for italic, and ~strikethrough~ in your messages.' },
              { title: 'Download the sample file', text: 'Click "Download Sample" on the upload screen to get a pre-formatted .xlsx with the right column headers.' },
              { title: 'Verify numbers first', text: 'Run your list through Number Verification before sending to cut down on failed deliveries.' },
              { title: 'One session per campaign', text: 'Each campaign runs on a single WhatsApp session. Split large lists across multiple campaigns if needed.' },
              { title: 'Smart delays protect your number', text: 'Walytic adds random delays between sends automatically. Do not try to bypass this by splitting into tiny campaigns.' },
            ].map((tip, i) => (
              <div key={i} className="bg-surface-card border border-surface-border rounded-lg p-4">
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prev / Next */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/crm" className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> CRM
          </Link>
          <Link to="/guide/number-verification" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            Next: Number Verification <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
